/**
 * /admin/security — Suspicious activity dashboard for a solo super-admin.
 *
 * Three pieces:
 *  1. KPI strip (open / critical / 24h / 7d).
 *  2. 7×24 heatmap of signals (last 7 days) with severity colouring.
 *  3. Signal feed — acknowledge / dismiss / apply recommended action.
 *  4. Config drawer — admin email + WA number + per-severity toggles.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Shield, ShieldAlert, ShieldCheck, KeyRound, Mail, MessageCircle, Settings as SettingsIcon,
    CheckCircle2, AlertTriangle, RotateCcw, Zap, X,
} from "lucide-react";
import { scorePassword, validatePassword } from "@/lib/passwordStrength";

const SEV_COLOR = {
    critical: "rose-500",
    high:     "amber-400",
    medium:   "primary",
    low:      "muted-foreground",
    ok:       "emerald-400",
};
const SEV_BG = {
    critical: "bg-rose-500/15 border-rose-500/40",
    high:     "bg-amber-500/15 border-amber-500/40",
    medium:   "bg-primary/10 border-primary/40",
    low:      "bg-card border-border",
};

export default function AdminSecurity() {
    const [summary, setSummary] = useState(null);
    const [signals, setSignals] = useState([]);
    const [heat, setHeat] = useState(null);
    const [filter, setFilter] = useState("open");
    const [showConfig, setShowConfig] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, sig, h] = await Promise.all([
                api.get("/admin/security/summary"),
                api.get("/admin/security/signals", { params: filter === "all" ? {} : { status: filter } }),
                api.get("/admin/security/heatmap"),
            ]);
            setSummary(s.data); setSignals(sig.data); setHeat(h.data);
        } catch (e) { toast.error(apiError(e)); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const runNow = async () => {
        setBusy(true);
        try { await api.post("/admin/security/run-now"); toast.success("Rule engine ran · refreshed"); load(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const sendDigest = async () => {
        setBusy(true);
        try { await api.post("/admin/security/digest/run-now"); toast.success("Digest sent to your email + WhatsApp"); }
        catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const ack = async (s) => {
        try { await api.post(`/admin/security/signals/${s.id}/ack`, { notes: "" }); toast.success("Acknowledged"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const dismiss = async (s) => {
        if (!window.confirm("Dismiss this signal? Marks it resolved without taking the recommended action.")) return;
        try { await api.post(`/admin/security/signals/${s.id}/dismiss`, { notes: "" }); toast.success("Dismissed"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const apply = async (s) => {
        const ra = s.recommended_action;
        if (!ra) return;
        const conf = window.prompt(`Apply auto-action: "${ra.label}"\n\nThis is irreversible and audit-logged. Type APPLY (uppercase) to confirm:`);
        if (conf !== "APPLY") { toast.info("Cancelled"); return; }
        try {
            const { data } = await api.post(`/admin/security/signals/${s.id}/apply-action`, { confirmation: "APPLY" });
            toast.success(`Action applied: ${data.action}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-5 min-w-0" data-testid="admin-security">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Solo super-admin · 24×7 watchman</div>
                    <h1 className="text-3xl text-foreground">Security signals</h1>
                    <p className="text-muted-foreground text-sm mt-1">Passive detector flags suspicious patterns. Email + WhatsApp + in-app push the moment something fires.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={runNow} disabled={busy} data-testid="security-run-now"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                        <RotateCcw className="w-3 h-3"/>Run now
                    </button>
                    <button onClick={sendDigest} disabled={busy} data-testid="security-send-digest"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                        <Mail className="w-3 h-3"/>Send digest now
                    </button>
                    <button onClick={() => setShowConfig(true)} data-testid="security-config-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                        <SettingsIcon className="w-3 h-3"/>Alert config
                    </button>
                </div>
            </div>

            {/* KPI strip */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Kpi label="Open" value={summary.open} severity={summary.open ? "high" : "ok"} testid="kpi-open"/>
                    <Kpi label="Critical · open" value={summary.critical_open} severity={summary.critical_open ? "critical" : "ok"} testid="kpi-critical"/>
                    <Kpi label="High · open" value={summary.high_open} severity={summary.high_open ? "high" : "ok"} testid="kpi-high"/>
                    <Kpi label="Last 24h" value={summary.last_24h} testid="kpi-24h"/>
                    <Kpi label="Last 7d" value={summary.last_7d} testid="kpi-7d"/>
                </div>
            )}

            {/* Heatmap */}
            {heat && <Heatmap data={heat}/>}

            {/* Signal feed */}
            <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    {["open", "acknowledged", "actioned", "dismissed", "all"].map(s => (
                        <button key={s} onClick={() => setFilter(s)}
                            data-testid={`filter-${s}`}
                            className={`px-3 py-1 text-[10px] uppercase tracking-[0.16em] border ${filter === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                            {s}
                        </button>
                    ))}
                </div>
                {signals.length === 0 && (
                    <div className="border border-emerald-500/20 bg-emerald-500/5 p-6 text-center" data-testid="no-signals">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2"/>
                        <div className="text-foreground">All quiet · no {filter} signals</div>
                    </div>
                )}
                {signals.map(s => <SignalRow key={s.id} sig={s} onAck={() => ack(s)} onDismiss={() => dismiss(s)} onApply={() => apply(s)}/>)}
            </div>

            <MuteRulesPanel/>

            <SuperAdminChangePasswordCard/>
            <PasswordPolicyPanel/>

            {showConfig && <ConfigDrawer onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); load(); }}/>}
        </div>
    );
}

function Kpi({ label, value, severity, testid }) {
    const c = SEV_COLOR[severity || "ok"];
    return (
        <div className="border border-border bg-card px-4 py-3" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
            <div className={`text-3xl font-mono text-${c}`}>{value}</div>
        </div>
    );
}

function Heatmap({ data }) {
    return (
        // Outer wrapper uses CSS Grid with minmax(0, 1fr) — this is the
        // bulletproof clamp pattern that prevents inner content from
        // pushing the parent past its grid-cell width, regardless of
        // the flex chain above.
        <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
            <div className="border border-border bg-card p-4" data-testid="security-heatmap" style={{ minWidth: 0 }}>
                <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Heatmap · last {data.days} days · IST</div>
                    <div className="text-[10px] flex gap-2 text-muted-foreground flex-wrap">
                        Severity:
                        <span className="text-rose-500">■</span>critical
                        <span className="text-amber-400">■</span>high
                        <span className="text-primary">■</span>medium
                        <span className="text-muted-foreground">■</span>low
                    </div>
                </div>
                <div style={{ overflowX: "auto", width: "100%" }}>
                    <table className="text-[9px] border-collapse" style={{ minWidth: "max-content" }}>
                        <thead>
                            <tr>
                                <th className="w-10"></th>
                                {data.hours.map(h => (
                                    <th key={h} className="px-1 py-0.5 text-muted-foreground">{String(h).padStart(2, "0")}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.grid.map((row, wd) => (
                                <tr key={wd}>
                                    <td className="px-1 text-muted-foreground">{data.weekdays[wd]}</td>
                                    {row.map((cell, hr) => (
                                        <td key={hr} title={`${data.weekdays[wd]} ${hr}:00 · ${cell.count} signal(s) · max ${cell.max_sev}`}
                                            className={`w-5 h-5 border border-card ${cell.count === 0 ? "" : `bg-${SEV_COLOR[cell.max_sev] || "card"}`}`}
                                            style={cell.count > 0 ? { opacity: 0.3 + Math.min(0.7, cell.count * 0.15) } : {}}>
                                            {cell.count > 0 ? <span className="text-[8px] text-black font-mono">{cell.count}</span> : ""}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SignalRow({ sig, onAck, onDismiss, onApply }) {
    const bg = SEV_BG[sig.severity] || SEV_BG.low;
    const Ic = sig.severity === "critical" ? ShieldAlert : (sig.severity === "high" ? AlertTriangle : Shield);
    const col = SEV_COLOR[sig.severity] || SEV_COLOR.low;
    return (
        <div className={`border ${bg} p-4 min-w-0`} data-testid={`signal-${sig.id}`}>
            <div className="flex items-start gap-3">
                <Ic className={`w-5 h-5 text-${col} shrink-0 mt-0.5`}/>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.16em]">
                        <span className={`text-${col} font-medium`}>{sig.severity}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-primary">{sig.rule_id}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{sig.status}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{new Date(sig.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-foreground mt-1">{sig.message}</div>
                    {sig.details && Object.keys(sig.details).length > 0 && (
                        <pre className="text-[10px] text-muted-foreground bg-background border border-card p-2 mt-2 overflow-x-auto">{JSON.stringify(sig.details, null, 2)}</pre>
                    )}
                    {sig.action_taken && (
                        <div className="text-[10px] mt-2 text-emerald-400">Action taken: {sig.action_taken} · {sig.actioned_at && new Date(sig.actioned_at).toLocaleString()}</div>
                    )}
                </div>
            </div>
            {sig.status === "open" && (
                <div className="flex gap-2 mt-3 ml-8 flex-wrap">
                    {sig.recommended_action && (
                        <button onClick={onApply} data-testid={`apply-${sig.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
                            <Zap className="w-3 h-3"/>Apply · {sig.recommended_action.label}
                        </button>
                    )}
                    <button onClick={onAck} data-testid={`ack-${sig.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-border text-muted-foreground hover:text-emerald-400 hover:border-emerald-400/40">
                        <CheckCircle2 className="w-3 h-3"/>Acknowledge
                    </button>
                    <button onClick={onDismiss} data-testid={`dismiss-${sig.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-border text-muted-foreground hover:text-foreground">
                        <X className="w-3 h-3"/>Dismiss
                    </button>
                </div>
            )}
        </div>
    );
}

function ConfigDrawer({ onClose, onSaved }) {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/admin/security/config").then(r => setCfg(r.data || {})); }, []);
    if (!cfg) return null;
    const save = async () => {
        setSaving(true);
        try { await api.put("/admin/security/config", cfg); toast.success("Saved"); onSaved(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-card border border-border w-full max-w-lg p-5 space-y-4" data-testid="security-config-drawer">
                <h3 className="text-lg text-foreground">Alert configuration</h3>
                <p className="text-[11px] text-muted-foreground">Solo-admin setup: where to push alerts and which severities trigger which channel.</p>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1 flex items-center gap-1.5"><Mail className="w-3 h-3"/>Admin email</div>
                    <input value={cfg.admin_email || ""} onChange={e => setCfg({...cfg, admin_email: e.target.value})}
                        placeholder="you@example.com"
                        data-testid="cfg-email"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1 flex items-center gap-1.5"><MessageCircle className="w-3 h-3"/>Admin WhatsApp (E.164 with +)</div>
                    <input value={cfg.admin_whatsapp || ""} onChange={e => setCfg({...cfg, admin_whatsapp: e.target.value})}
                        placeholder="+919876543210"
                        data-testid="cfg-wa"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-card">
                    {[
                        ["email_enabled", "Email channel"],
                        ["whatsapp_enabled", "WhatsApp channel"],
                        ["in_app_notify", "In-app bell"],
                        ["notify_critical", "Push CRITICAL"],
                        ["notify_high", "Push HIGH"],
                        ["notify_medium", "Push MEDIUM"],
                        ["notify_low", "Push LOW"],
                        ["daily_digest_enabled", "Daily digest"],
                        ["quiet_hours_enabled", "Quiet hours"],
                    ].map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 text-xs text-[#E0E0E0]">
                            <input type="checkbox" checked={!!cfg[k]} onChange={e => setCfg({...cfg, [k]: e.target.checked})}
                                data-testid={`cfg-${k}`}
                                className="accent-primary"/>
                            {label}
                        </label>
                    ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Daily digest hour (IST)</div>
                        <input value={cfg.daily_digest_hour_ist ?? 9} onChange={e => setCfg({...cfg, daily_digest_hour_ist: parseInt(e.target.value) || 0})}
                            type="number" min={0} max={23} className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Quiet start (IST hour)</div>
                        <input value={cfg.quiet_start_ist ?? 23} onChange={e => setCfg({...cfg, quiet_start_ist: parseInt(e.target.value) || 0})}
                            type="number" min={0} max={23} className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Quiet end (IST hour)</div>
                        <input value={cfg.quiet_end_ist ?? 7} onChange={e => setCfg({...cfg, quiet_end_ist: parseInt(e.target.value) || 0})}
                            type="number" min={0} max={23} className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                </div>
                <div className="text-[10px] text-muted-foreground">During quiet hours, only <span className="text-rose-400">CRITICAL</span> alerts ping email + WhatsApp. Lower-severity items queue silently into the in-app bell so you see them in the morning digest.</div>

                <div className="flex justify-end gap-2 pt-2 border-t border-card">
                    <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="cfg-save"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Mute Rules — fully configurable auto-mute by regex
// ---------------------------------------------------------------------------
function MuteRulesPanel() {
    const [rules, setRules] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const load = useCallback(() => {
        api.get("/admin/security/mute-rules").then(r => setRules(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);
    const del = async (id) => {
        if (!window.confirm("Delete this mute rule? Future occurrences of the pattern will surface as normal again.")) return;
        try { await api.delete(`/admin/security/mute-rules/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const toggle = async (id) => {
        try { await api.patch(`/admin/security/mute-rules/${id}/toggle`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="border border-border bg-card p-4 space-y-3" data-testid="mute-rules-panel">
            <div className="flex justify-between items-center">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Auto-mute rules · {rules.length}</div>
                    <div className="text-xs text-muted-foreground mt-1">Recurring benign patterns (e.g. your own dynamic-IP triggering R-10 daily) silently auto-dismiss after N prior dismissals.</div>
                </div>
                <button onClick={() => setShowAdd(true)} data-testid="mute-rule-add"
                    className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10">+ New rule</button>
            </div>
            {rules.length === 0 && <div className="text-muted-foreground text-xs italic">No mute rules yet</div>}
            {rules.map(r => (
                <div key={r.id} className="border border-card bg-background p-3 flex items-start gap-3" data-testid={`mute-rule-${r.id}`}>
                    <input type="checkbox" checked={r.active} onChange={() => toggle(r.id)} className="accent-primary mt-1"/>
                    <div className="flex-1 text-xs">
                        <div className="text-foreground">{r.label || `${r.rule_id} · pattern ${r.dedupe_key_pattern}`}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{r.rule_id} · /{r.dedupe_key_pattern}/ · ≥{r.threshold_dismissals} prior dismissals within {r.window_days}d · muted {r.auto_mute_count || 0}×</div>
                    </div>
                    <button onClick={() => del(r.id)} data-testid={`mute-rule-del-${r.id}`} className="text-rose-400 hover:text-rose-300 text-[10px]">Delete</button>
                </div>
            ))}
            {showAdd && <AddMuteRule onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }}/>}
        </div>
    );
}

function AddMuteRule({ onClose, onCreated }) {
    const [form, setForm] = useState({ rule_id: "R-10", dedupe_key_pattern: "",
                                          threshold_dismissals: 2, window_days: 7, label: "" });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try { await api.post("/admin/security/mute-rules", form);
            toast.success("Mute rule created · matching future signals auto-dismiss");
            onCreated();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-lg p-5 space-y-3"
                data-testid="mute-rule-add-modal">
                <h3 className="text-lg text-foreground">New auto-mute rule</h3>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Rule ID</div>
                    <select value={form.rule_id} onChange={e => setForm({...form, rule_id: e.target.value})}
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm">
                        {["R-01","R-02","R-03","R-04","R-05","R-06","R-07","R-08","R-09","R-10"].map(r => <option key={r}>{r}</option>)}
                    </select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Dedupe-key regex *</div>
                    <input value={form.dedupe_key_pattern} onChange={e => setForm({...form, dedupe_key_pattern: e.target.value})}
                        required placeholder="e.g. ^R10-fp:youremail@:abc"
                        data-testid="mute-pattern"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                    <div className="text-[10px] text-muted-foreground mt-1">Regex matched against each new signal's dedupe_key. Use the dedupe_key from a sample dismissed signal.</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Threshold (prior dismissals)</div>
                        <input value={form.threshold_dismissals} onChange={e => setForm({...form, threshold_dismissals: parseInt(e.target.value) || 2})}
                            type="number" min={1} className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Window (days)</div>
                        <input value={form.window_days} onChange={e => setForm({...form, window_days: parseInt(e.target.value) || 7})}
                            type="number" min={1} className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Label (optional)</div>
                    <input value={form.label} onChange={e => setForm({...form, label: e.target.value})}
                        placeholder="Mute my own laptop's dynamic-IP R-10 trips"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="mute-rule-submit"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {saving ? "Saving…" : "Create rule"}
                    </button>
                </div>
            </form>
        </div>
    );
}


// =============================================================================
// SuperAdminChangePasswordCard — gives super_admin a place to rotate creds
// without needing the tenant /app/settings route they can't access.
// =============================================================================
function SuperAdminChangePasswordCard() {
    const [form, setForm] = useState({ current: "", next: "", confirm: "" });
    const [status, setStatus] = useState(null);
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/auth/password-status").then(r => setStatus(r.data)).catch(() => {});
    }, []);

    const score = scorePassword(form.next);
    const matches = form.next && form.next === form.confirm;
    const days = status ? Math.max(0, Math.ceil(status.days_remaining)) : null;
    const expColor = !status ? "#A3A3A3"
        : status.expired ? "#ef4444"
        : status.must_change_now ? "#ef4444"
        : status.in_warning_window ? "#f59e0b"
        : "#22c55e";

    const submit = async (e) => {
        e.preventDefault();
        const err = validatePassword(form.next);
        if (err) { toast.error(err); return; }
        if (!matches) { toast.error("Passwords don't match"); return; }
        if (form.current === form.next) { toast.error("New password must differ from current."); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
            toast.success("Password changed. Please sign in again.");
            sessionStorage.removeItem("lawos.pwexp.skipped");
            setTimeout(() => { window.location.href = "/login"; }, 800);
        } catch (e2) { toast.error(apiError(e2)); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="admin-change-password-card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">My password</div>
                </div>
                {status && (
                    <div className="text-[10px] font-mono" style={{color: expColor}} data-testid="admin-change-password-status-pill">
                        {status.expired ? "EXPIRED · change now" : `Expires in ${days} day${days === 1 ? "" : "s"}`}
                    </div>
                )}
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Rotate the super-admin credentials. New password must satisfy the platform policy below. Last 5 passwords blocked from reuse.
            </p>
            <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3">
                <Field label="Current password">
                    <input type={show ? "text" : "password"} value={form.current} onChange={e=>setForm({...form, current: e.target.value})} required
                        data-testid="admin-pwd-current-input"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                </Field>
                <Field label="New password" hint={form.next ? score.hint : "12+ chars, letters and digits."}>
                    <input type={show ? "text" : "password"} value={form.next} onChange={e=>setForm({...form, next: e.target.value})} required
                        data-testid="admin-pwd-new-input"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                    {form.next && (
                        <div className="flex items-center gap-2 mt-1.5" data-testid="admin-pwd-strength">
                            <div className="flex-1 h-1 bg-border overflow-hidden">
                                <div className="h-full transition-all" style={{width: `${((score.score+1)/6)*100}%`, background: score.color}}/>
                            </div>
                            <div className="text-[10px] font-medium" style={{color: score.color}}>{score.label}</div>
                        </div>
                    )}
                </Field>
                <Field label="Confirm new password">
                    <input type={show ? "text" : "password"} value={form.confirm} onChange={e=>setForm({...form, confirm: e.target.value})} required
                        data-testid="admin-pwd-confirm-input"
                        className={`w-full bg-background border outline-none px-3 py-2 text-xs ${form.confirm ? (matches ? "border-emerald-500/60" : "border-red-500/60") : "border-border focus:border-primary"}`}/>
                </Field>
                <div className="sm:col-span-3 flex items-center justify-between pt-2">
                    <label className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={show} onChange={e=>setShow(e.target.checked)} className="accent-primary" data-testid="admin-pwd-show-toggle"/>
                        Show passwords
                    </label>
                    <button type="submit" disabled={busy || !form.current || !form.next || !matches}
                        data-testid="admin-pwd-save-btn"
                        className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                        {busy ? "Changing…" : "Change password"}
                    </button>
                </div>
            </form>
        </div>
    );
}

// =============================================================================
// PasswordPolicyPanel — per-role rotation period + reuse-block count
// =============================================================================
function PasswordPolicyPanel() {
    const [policy, setPolicy] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/password-policy")
            .then(r => { setPolicy(r.data); setDraft(JSON.parse(JSON.stringify(r.data))); })
            .catch(e => toast.error(apiError(e)));
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put("/admin/password-policy", draft);
            setPolicy(r.data);
            setDraft(JSON.parse(JSON.stringify(r.data)));
            toast.success("Password policy saved · applies on next login of each user");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    if (!policy || !draft) return (
        <div className="bg-muted border border-border p-6">
            <div className="text-xs text-muted-foreground">Loading password policy…</div>
        </div>
    );

    const dirty = JSON.stringify(policy) !== JSON.stringify(draft);
    const ROLES = ["super_admin", "firm_owner", "managing_partner", "advocate", "junior_advocate", "paralegal", "accountant", "client_portal"];

    return (
        <div className="bg-muted border border-border p-6" data-testid="password-policy-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Password policy · per-role rotation</div>
                </div>
                {dirty && <span className="text-[10px] text-amber-400 font-mono">unsaved changes</span>}
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Set how often each role must rotate their password. Warning popup shows in the last N days (skippable until the final mandatory day).
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {ROLES.map(role => (
                    <Field key={role} label={`${role.replace(/_/g, " ")} (days)`}>
                        <input type="number" min="7" max="365" value={draft.rotation_days_by_role[role] ?? 45}
                            onChange={e => setDraft({...draft, rotation_days_by_role: {...draft.rotation_days_by_role, [role]: parseInt(e.target.value) || 45}})}
                            data-testid={`pwd-policy-rot-${role}`}
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                    </Field>
                ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <Field label="Warning window (days)" hint="Popup starts showing">
                    <input type="number" min="1" max="30" value={draft.warning_window_days}
                        onChange={e => setDraft({...draft, warning_window_days: parseInt(e.target.value) || 5})}
                        data-testid="pwd-policy-warning-days"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                </Field>
                <Field label="Mandatory window (days)" hint="Popup forces change">
                    <input type="number" min="1" max="7" value={draft.mandatory_window_days}
                        onChange={e => setDraft({...draft, mandatory_window_days: parseInt(e.target.value) || 1})}
                        data-testid="pwd-policy-mandatory-days"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                </Field>
                <Field label="Reuse-block depth" hint="Last N passwords blocked">
                    <input type="number" min="0" max="20" value={draft.reuse_check_count}
                        onChange={e => setDraft({...draft, reuse_check_count: parseInt(e.target.value) || 0})}
                        data-testid="pwd-policy-reuse-count"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                </Field>
                <Field label="Min length" hint="Min characters required">
                    <input type="number" min="8" max="64" value={draft.min_length}
                        onChange={e => setDraft({...draft, min_length: parseInt(e.target.value) || 12})}
                        data-testid="pwd-policy-min-length"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                </Field>
            </div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setDraft(JSON.parse(JSON.stringify(policy)))} disabled={!dirty || busy}
                    data-testid="pwd-policy-reset"
                    className="px-3 py-2 border border-border text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-[0.16em] disabled:opacity-30">
                    Reset
                </button>
                <button type="button" onClick={save} disabled={!dirty || busy}
                    data-testid="pwd-policy-save"
                    className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                    {busy ? "Saving…" : "Save policy"}
                </button>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">{label}</label>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

